import queryString from 'query-string';

import { validateQueryParameters } from '~/helpers/verifyQueryParameters';
import { createAxiosInstance } from '~/plugins/axios.plugin';

export class CoursesService {
  static SERVICE_NAME = 'courses';

  constructor() {
    this.client = createAxiosInstance({
      baseURL        : process.env.PLATAFORMA_API,
      useAccessToken : true
    });

    this.clientV2 = createAxiosInstance({
      baseURL        : process.env.PLATAFORMA_API,
      useAccessToken : true,
      customHeaders  : {
        'maisaedu-api-version': '20240531.1'
      }
    });
  }

  getEnrolledCourses(state, filter, period, page, limit, type, categories) {
    const stringified = queryString.stringify({ state, filter, period, page, limit, type, categories }, { skipNull: true, skipEmptyString: true });

    let url = '/academic/courses/me';

    if (stringified) {
      url += `?${stringified}`;
    }

    return this.client.get(url);
  }

  getEnrolledCoursesV2(
    {
      state,
      filter,
      period,
      page,
      limit,
      sort,
      sortBy,
      type,
      categories,
      startDate,
      endDate,
      courseTypeIds,
      approvalFlowStatusId
    },
    headers = {}
  ) {
    const savedSignature = localStorage.getItem("plataforma_noticeToken");

    if (savedSignature) {
      try {
        const signatureData = JSON.parse(savedSignature);
        if (signatureData) {
          headers["x-notice-show-modal"] = signatureData.shouldShowModal;
          headers["x-notice-expired-at"] = signatureData.expiredAt;
          headers["x-notice-signature"] = signatureData.signature;
        } else {
          localStorage.removeItem("plataforma_noticeToken");
        }
      } catch (e) {
        localStorage.removeItem("plataforma_noticeToken");
      }
    }

    const params = {
      state,
      filter,
      period,
      page,
      limit,
      sort,
      sortBy,
      type,
      categories,
      startDate,
      endDate,
      courseTypeIds,
      approvalFlowStatusId
    };

    const queryParams = validateQueryParameters(params);

    return this.clientV2.get(`/academic/courses/me?${queryParams}`, { headers });
  }

  getPermissionByCourse(id) {
    return this.client.get(`/academic/courses/${id}/permissions/me`);
  }

  getAllCourses({
    type,
    search,
    perPage,
    page,
    order,
    sortBy,
    categoryIds,
    periodId,
    isActive,
    onlyWithContent,
    startAt,
    endAt,
    showAcademicsWithoutDate,
    pagination = true,
    approvalFlowStatusId,
    syncTypeId,
    masterAcademicMainId
  }) {
    const params = {
      type,
      search,
      perPage,
      page,
      order,
      sortBy,
      periodId,
      isActive,
      onlyWithContent,
      startAt,
      endAt,
      showAcademicsWithoutDate,
      pagination,
      approvalFlowStatusId,
      syncTypeId,
      masterAcademicMainId
    };

    const queryParams = validateQueryParameters(params);
    return this.client.post(`academic/courses/search?${queryParams}`, { categoryIds });
  }

  createCourse(course) {
    return this.client.post('academic/courses', course);
  }

  getCourseById(courseId) {
    return this.client.get(`academic/courses/${courseId}`);
  }

  editCourse(course) {
    return this.client.put(`academic/courses/${course.id}`, course);
  }

  deleteCourse(id) {
    return this.client.delete(`academic/courses/${id}`);
  }

  getCoursesForCopyContent(limit, page, filter, order, type, academicMainId, onlyWithContent, categoryIds, periodId, notIncludeAcademicMainId, notIncludeAcademicMainIds) {
    const params = {
      limit,
      page,
      filter,
      order,
      type,
      academicMainId,
      onlyWithContent,
      categoryIds,
      periodId,
      notIncludeAcademicMainId
    };

    const queryParams = validateQueryParameters(params);

    if (notIncludeAcademicMainIds) {
      return this.client.post(`academic/courses/import-list${queryParams && `?${queryParams}`}`, { notIncludeAcademicMainIds });
    }

    return this.client.get(`academic/courses/import${queryParams && `?${queryParams}`}`);
  }

  getCoursesForBatchProcess(params, file = null) {
    const queryParams = validateQueryParameters(params);

    const formData = new FormData();
    formData.append('file', file);

    return this.client.post(`/academic/batches/courses?${queryParams}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  sendAvaliaIntegration(academicMainId) {
    return this.client.post(`/academic/integrations/model/${academicMainId}`);
  }

  getCourseEnrolledRoles(courseId) {
    return this.client.get(`academic/courses/${courseId}/roles`);
  }

  getBatchesTopics({ params, payload }) {
    let file = null;
    if (params.academicFile) {
      file = params.academicFile;
      delete params.academicFile;
    }

    const queryParams = validateQueryParameters(params);
    const formData = new FormData();
    formData.append('data', JSON.stringify(payload));
    formData.append('file', file);

    return this.client.post(
      `academic/batches/courses/topics?${queryParams}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  accessStudentMode(academicMainId) {
    return this.client.get(`academic/academics-main/${academicMainId}/student-view`);
  }

  logoutStudentMode(academicMainId) {
    return this.client.delete(`academic/academics-main/${academicMainId}/student-view`);
  }

  favoriteCourse(courseId) {
    return this.client.post(`academic/courses/${courseId}/favorite`);
  }

  unfavoriteCourse(courseId) {
    return this.client.delete(`academic/courses/${courseId}/favorite`);
  }

  getCourseTypes(academicMainTypeId) {
    return this.clientV2.get(`academic/courses/course-type/${academicMainTypeId}/me`);
  }

  patch(params) {
    return this.client.patch(`academic/courses/${params.academicMainId}/content`, params.data);
  }

  getModelCourseComments(academicMainId) {
    return this.client.get(`academic/courses/${academicMainId}/comments`);
  }

  patchIsNotifySyncUpdateEnabled(academicMainId, isNotifySyncUpdateEnabled) {
    return this.client.patch(`academic/courses/${academicMainId}/sync-notification`, {
      isNotifySyncUpdateEnabled
    });
  }

  getCoursesToImport({
    search,
    perPage,
    page,
    order,
    sortBy,
    categoryIds,
    periodId,
    originAcademicSyncTypeId,
    pagination = true
  }) {
    const params = {
      originAcademicSyncTypeId,
      search,
      perPage,
      page,
      order,
      sortBy,
      periodId,
      pagination
    };

    const queryParams = validateQueryParameters(params);
    return this.client.post(`academic/courses/available-courses/import?${queryParams}`, { categoryIds });
  }
}

export default new CoursesService();
